import dayjs from 'dayjs';
import cookie from 'js-cookie';
import type { PdpContext } from 'types';
import type { CapiProductPage } from 'types/capi/pdp';
import type { User } from 'types/User';
import * as uuid from 'uuid';

import braze from '~/services/braze.client';
import facebook from '~/services/facebook';
import facebookPixel from '~/services/facebook.pixel';
import gtm from '~/services/gtm.client';
import tiktokServer from '~/services/tiktok';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import { sha256 } from '~/utils/hash';
import {
  getEntrypoint,
  getPreviousContext,
  getPreviousPagePath,
  getPreviousPagePosition,
  getPreviousPageType,
  getSourceAndMedium
} from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  clientIp: string | null;
  isBot: boolean;
  product: CapiProductPage;
  user: User;
};

export default class PdpViewEvent extends UserEvent<EventData> {
  private readonly eventId = uuid.v4();
  private readonly pdpContext = getPreviousContext<PdpContext>();

  async sendToFacebook() {
    const eventId = this.eventId;

    await facebookPixel.fireEvent({
      data: {
        content_ids: [this.getProductId()],
        content_type: 'product_group',
        currency: this.getCurrency(),
        value: this.getPrice()
      },
      eventID: eventId,
      eventName: facebookPixel.StandardEventNames.ViewContent
    });

    const hashedProfile = await facebook.getHashedProfile(
      this.data.user.profile
    );

    const externalId = await sha256(this.data.user.id ?? 'guest');

    const serverEventData = [
      {
        action_source: 'website',
        custom_data: {
          brand: this.data.product.brand ?? '',
          content_ids: [this.data.product.product_id],
          content_name: this.data.product.name ?? '',
          content_type: 'product_group',
          currency: this.data.product.price?.currency ?? '',
          value:
            this.data.product.price?.sale ??
            this.data.product.price.regular ??
            0
        },
        event_id: eventId,
        event_name: facebookPixel.StandardEventNames.ViewContent,
        event_source_url: this.data.product.canonical_url,
        event_time: dayjs().unix(),
        user_data: {
          client_ip_address: this.data.clientIp,
          client_user_agent: navigator?.userAgent,
          external_id: externalId,
          fbc: cookie.get('_fbc'),
          fbp: cookie.get('_fbp'),
          ...hashedProfile
        }
      }
    ];

    this.serverEvents.push({ data: serverEventData, type: 'facebook' });
  }

  async sendToTikTok() {
    const eventId = uuid.v4();
    const hashedProfile = await tiktokServer.getHashedProfile(
      this.data.user.profile
    );

    this.serverEvents.push({
      data: [
        {
          context: {
            ip: this.data.clientIp ?? '',
            page: { url: this.data.product.canonical_url },
            user_agent: navigator?.userAgent ?? ''
          },
          event: 'ViewContent',
          event_id: eventId,
          event_time: Math.floor(Date.now() / 1000),
          properties: {
            contents: [
              {
                content_id: String(this.getProductId()),
                content_type: 'product'
              }
            ],
            currency: this.getCurrency(),
            value: this.getPrice()
          },
          user: {
            external_id: this.data.user.id ?? 'guest',
            ttclid: cookie.get('tss_ttclid'),
            ttp: cookie.get('_ttp'),
            ...hashedProfile
          }
        }
      ],
      type: 'tiktok'
    });

    tiktok.fireEvent(
      'ViewContent',
      {
        content_ids: [this.getProductId()],
        content_type: 'product',
        currency: this.getCurrency(),
        value: this.getPrice()
      },
      eventId
    );
  }

  collectServerEvents() {
    const serverEventData = {
      currency: this.getCurrency(),
      event_name: 'pdp_view' as const,
      has_sale_price: this.getHasSalePrice(),
      is_logged_in: this.data.user.isLoggedIn,
      item: this.getItem(),
      num_of_listings: this.getNumOfListings(),
      position: this.pdpContext?.position,
      product_brand: this.data.product.brand,
      product_id: this.data.product.product_id,
      product_name: this.data.product.name,
      product_sub_brand: this.data.product._event_data.gtm.productSubBrand,
      product_view_price: this.getPrice(),
      stock_status: this.data.product.stock_status,
      style_code: this.data.product.style_code,
      ...this.getSharedParams()
    };

    this.serverEvents.push({ data: serverEventData, type: 'pdp_view' });
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.ViewContent, {
      contents: [{ content_id: this.getProductId(), content_type: 'product' }],
      currency: this.getCurrency(),
      value: this.getPrice()
    });
  }

  sendToGtm() {
    return gtm.Ecommerce.viewListItem(
      this.data.product,
      this.pdpContext?.position,
      {
        num_of_listings: this.getNumOfListings(),
        product_brand: this.data.product.brand,
        product_id: this.data.product.product_id,
        product_view_price: this.getPrice(),
        style_code: this.data.product.style_code,
        user_id: this.data.user.id,
        ...this.getSharedParams()
      }
    );
  }

  sendToBraze(): void {
    return braze.logPdpViewEvent(this.data.product._event_data.marketing);
  }

  private getCurrency() {
    return this.data.product.price.currency;
  }

  private getItem() {
    return {
      index: this.pdpContext?.position,
      item_brand: this.data.product.brand,
      item_category: this.data.product.genders?.join(', '),
      item_category2: this.data.product.sub_type,
      item_id: this.data.product.style_code,
      item_name: this.data.product.name,
      item_variant: this.data.product.model,
      location_id: this.data.product.stock_status,
      price:
        this.data.product.price.sale ?? this.data.product.price.regular ?? null
    };
  }

  private getSharedParams() {
    return {
      entry_point: getEntrypoint(),
      page_path: window.location.pathname,
      page_type: 'pdp' as const,
      platform: 'web' as const,
      previous_page_path: getPreviousPagePath(),
      previous_page_position: getPreviousPagePosition(),
      previous_page_type: getPreviousPageType(),
      source_medium: getSourceAndMedium()
    };
  }

  private getNumOfListings() {
    return this.data.product.stock_listings.affiliates.length;
  }

  private getHasSalePrice() {
    return this.data.product.price.sale != null;
  }

  private getPrice() {
    return (
      this.data.product.price.sale ??
      this.data.product.price.regular ??
      undefined
    );
  }

  private getProductId() {
    let productId = `${this.data.product.product_id}`;

    if (this.data.product.sub_type === 'clothing') {
      productId += `-${this.data.product.variant_id}`;
    }

    return productId;
  }
}
